<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: DNARelationNetwork                                    |
| Section: components                                              |
| Purpose: Interactive DNA relationship network with filters.      |
+------------------------------------------------------------------+
-->
<template>
    <div class="dna-relation-network">
        <div class="dna-network-sidebar">
            <h1>{{ filteredPersonList.length }} DNA-profielen</h1>

            <div class="dna-network-sidebar-intro">
                <p>{{ testerList.length }} testers gekoppeld aan {{ activeLinkCount }} actieve matches.</p>
            </div>

            <div class="dna-network-tester-filters">
                <button v-for="tester in testerList" :key="tester.id" class="dna-network-tester-toggle"
                    :class="{ inactive: !isTesterActive(tester.id) }" :style="getBadgeStyle(tester.id)"
                    @click="toggleTesterFilter(tester.id)">
                    {{ tester.id }}
                </button>
            </div>

            <div class="dna-network-sidebar-search">
                <input v-model="searchQuery" type="text" placeholder="Zoek op profiel…">
            </div>

            <div v-for="person in filteredPersonList" :key="person.id" class="dna-network-person-item"
                :class="{ highlighted: isSelectedPerson(person.id) }" @click="selectPerson(person.id, false)">
                <div class="dna-network-person-item-main">
                    <div>
                        <p class="dna-network-person-id">{{ getPersonDisplayName(person.id) }}</p>
                        <p class="dna-network-person-meta">
                            Sterkste match: {{ formatWeight(person.highestWeight, person.highestWeightUnit) }} via {{
                                person.highestTester }}
                        </p>
                    </div>
                    <span class="dna-network-person-badge" :style="getBadgeStyle(person.highestTester)">
                        {{ person.matches.length }}
                    </span>
                </div>

                <div v-if="isSelectedPerson(person.id)" class="dna-network-person-expanded">
                    <p class="dna-network-secondary-label" v-if="secondaryNodes.length">
                        Directe testers:
                    </p>
                    <ul v-if="secondaryNodes.length" class="dna-network-secondary-list">
                        <li v-for="secondary in secondaryNodes" :key="secondary" class="dna-network-secondary-item">
                            {{ secondary }}
                        </li>
                    </ul>

                    <div class="dna-network-match-groups">
                        <div v-for="match in selectedPersonMatches" :key="match.id" class="dna-network-match-group"
                            :data-tester="match.tester.toLowerCase()">
                            <span class="dna-network-match-group-label" :style="getMatchLabelStyle(match.tester)">
                                <span class="dna-network-color-dot" :style="getTesterDotStyle(match.tester)"></span>
                                {{ match.tester }}
                            </span>
                            <div class="dna-network-match-group-data">
                                <p><strong>{{ $t('dnaMatch.relation') }}:</strong> {{
                                    translateRelation(match.dnaInfo.relation) || '-' }}</p>
                                <p><strong>{{ $t('dnaMatch.sharedDNA') }}:</strong> {{ match.dnaInfo.sharedDNA || '-' }}
                                </p>
                                <p><strong>{{ $t('dnaMatch.segments') }}:</strong> {{ match.dnaInfo.sharedSegments ||
                                    '-' }}</p>
                                <p><strong>{{ $t('dnaMatch.largestSegment') }}:</strong> {{ match.dnaInfo.largestSegment
                                    || '-' }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="dna-network-canvas" ref="networkCanvas"></div>

        <aside v-if="activeDetails" class="dna-network-details-panel">
            <template v-if="activeDetails.type === 'person'">
                <div class="dna-network-details-header">
                    <div>
                        <p class="dna-network-details-eyebrow">DNA-profiel</p>
                        <h2>{{ getPersonDisplayName(activeDetails.id) }}</h2>
                    </div>
                    <p class="dna-network-details-summary">
                        {{ activePersonMatches.length }} tester{{ activePersonMatches.length === 1 ? '' : 's' }}
                    </p>
                </div>

                <div class="dna-network-match-groups dna-network-match-groups--panel">
                    <div v-for="match in activePersonMatches" :key="match.id" class="dna-network-match-group"
                        :data-tester="match.tester.toLowerCase()">
                        <span class="dna-network-match-group-label" :style="getMatchLabelStyle(match.tester)">
                            <span class="dna-network-color-dot" :style="getTesterDotStyle(match.tester)"></span>
                            {{ match.tester }}
                        </span>
                        <div class="dna-network-match-group-data">
                            <p><strong>{{ $t('dnaMatch.relation') }}:</strong> {{
                                translateRelation(match.dnaInfo.relation) || '-' }}</p>
                            <p><strong>{{ $t('dnaMatch.sharedDNA') }}:</strong> {{ match.dnaInfo.sharedDNA || '-' }}</p>
                            <p><strong>{{ $t('dnaMatch.segments') }}:</strong> {{ match.dnaInfo.sharedSegments || '-' }}
                            </p>
                            <p><strong>{{ $t('dnaMatch.largestSegment') }}:</strong> {{ match.dnaInfo.largestSegment ||
                                '-' }}</p>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="dna-network-details-header">
                    <div>
                        <p class="dna-network-details-eyebrow">Tester</p>
                        <h2>{{ activeDetails.id }}</h2>
                    </div>
                    <p class="dna-network-details-summary">
                        {{ activeTesterSummary.matchCount }} matches
                    </p>
                </div>

                <div class="dna-network-tester-summary">
                    <p><strong>Sterkste match:</strong> {{ activeTesterSummary.strongestMatchId || '-' }}</p>
                    <p><strong>Hoogste waarde:</strong> {{ formatWeight(activeTesterSummary.strongestWeight,
                        activeTesterSummary.strongestWeightUnit) }}</p>
                </div>

                <ul class="dna-network-top-matches">
                    <li v-for="match in activeTesterSummary.topMatches" :key="match.id">
                        <span>{{ getPersonDisplayName(match.personID) }}</span>
                        <strong>{{ formatWeight(match.weightValue, match.weightUnit) }}</strong>
                    </li>
                </ul>
            </template>
        </aside>

        <div v-show="tooltip.visible" class="dna-network-tooltip"
            :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }" v-html="tooltip.content"></div>
    </div>
</template>

<script>
import * as d3 from "d3";
import { getPersonById } from "@/api/personService";
import { useAuth } from "@/composables/useAuth";
import { translateDnaRelation } from "@/utils/dnaRelationI18n";

const DEFAULT_TESTER_COLOR = "#8c8c8c";
const DNA_TESTER_STYLES = [
    { badgeBg: "#EBF4FA", badgeText: "#2E5F7A", accent: "#4A7FA5" },
    { badgeBg: "#EDF5ED", badgeText: "#345934", accent: "#5A8A5A" },
    { badgeBg: "#F3EEF9", badgeText: "#533E72", accent: "#7B5EA7" },
    { badgeBg: "#FDF3E7", badgeText: "#7A481A", accent: "#C07228" },
    { badgeBg: "#FDEFF3", badgeText: "#7A2E45", accent: "#B24768" },
    { badgeBg: "#FBECEC", badgeText: "#8F2B35", accent: "#C84552" },
];
export default {
    name: "DNARelationNetwork",
    props: {
        matches: {
            type: Array,
            default: () => [],
        },
        testers: {
            type: Object,
            default: () => ({}),
        },
        testerOrder: {
            type: Array,
            default: () => [],
        },
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return {
            // D3 simulation and derived graph model used by both canvas and side panels.
            simulation: null,
            graphData: null,
            personLookup: {},
            testerLookup: {},
            personList: [],
            testerList: [],
            linkCount: 0,
            testerColorMap: {},
            activeTesters: {},
            searchQuery: "",
            personNameMap: {},
            selectedNode: null,
            hoveredNode: null,
            secondaryNodes: [],
            tooltip: {
                visible: false,
                x: 0,
                y: 0,
                content: "",
            },
            testerAnchorMap: {},
            resizeObserver: null,
        };
    },
    computed: {
        testerRootToId() {
            return Object.entries(this.testers).reduce((map, [testerID, config]) => {
                if (config?.rootPersonID) {
                    map[config.rootPersonID] = testerID;
                }
                return map;
            }, {});
        },
        isAuthenticated() {
            return Boolean(this.authToken);
        },
        filteredPersonList() {
            // Sidebar list mirrors active tester filters and optional text search.
            const query = this.searchQuery.trim().toLowerCase();

            return this.personList.filter((person) => {
                if (!this.isPersonVisible(person.id)) {
                    return false;
                }

                if (!query) {
                    return true;
                }

                const name = this.getPersonDisplayName(person.id).toLowerCase();
                return person.id.toLowerCase().includes(query) || name.includes(query);
            });
        },
        activeLinkCount() {
            if (!this.graphData || !this.graphData.links) {
                return 0;
            }

            return this.graphData.links.filter((link) => this.isTesterActive(link.tester)).length;
        },
        selectedPersonMatches() {
            if (!this.selectedNode || this.selectedNode.type !== "person") {
                return [];
            }

            return this.getPersonMatches(this.selectedNode.id, true);
        },
        activeDetails() {
            return this.hoveredNode || this.selectedNode;
        },
        activePersonMatches() {
            if (!this.activeDetails || this.activeDetails.type !== "person") {
                return [];
            }

            return this.getPersonMatches(this.activeDetails.id, true);
        },
        activeTesterSummary() {
            if (!this.activeDetails || this.activeDetails.type !== "tester") {
                return {
                    matchCount: 0,
                    strongestMatchId: null,
                    strongestWeight: 0,
                    strongestWeightUnit: "cM",
                    topMatches: [],
                };
            }

            return this.getTesterSummary(this.activeDetails.id);
        },
    },
    watch: {
        matches() {
            this.loadDNAMatches();
        },
    },
    mounted() {
        // Build the graph once mounted so we can measure canvas dimensions.
        this.loadDNAMatches();
        if (this.$refs.networkCanvas && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.resizeNetwork());
            this.resizeObserver.observe(this.$refs.networkCanvas);
        }
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        if (this.simulation) {
            this.simulation.stop();
        }
    },
    methods: {
        translateRelation(value) {
            const locale = this.$i18n?.locale?.value || this.$i18n?.locale || "nl";

            return translateDnaRelation(
                value,
                this.$t.bind(this),
                this.$te ? this.$te.bind(this) : null,
                {
                    warnOnMissing: process.env.NODE_ENV !== "production",
                    locale,
                }
            );
        },
        async loadDNAMatches() {
            try {
                // Normalize raw records into one graph structure reused by rendering,
                // filtering, details panel, and tooltip logic.
                this.graphData = this.buildGraphData(this.matches);
                this.personLookup = this.graphData.personLookup;
                this.testerLookup = this.graphData.testerLookup;
                this.personList = this.graphData.personNodes;
                this.testerList = this.graphData.testerNodes;
                this.linkCount = this.graphData.links.length;
                this.testerColorMap = this.assignTesterColors(this.testerList.map((tester) => tester.id));
                this.activeTesters = this.testerList.reduce((filters, tester) => {
                    filters[tester.id] = true;
                    return filters;
                }, {});

                d3.select(this.$refs.networkCanvas).selectAll("*").remove();
                this.createDNARelationNetwork(this.graphData);
                await this.loadPersonNames();
                this.refreshPersonLabels();
            } catch (error) {
                console.error("Error loading DNA matches:", error);
                alert("DNA-netwerk kon niet worden geladen. Controleer de console voor details.");
            }
        },
        async loadPersonNames() {
            if (!this.isAuthenticated) {
                // Keep person IDs anonymized when no auth token is present.
                this.personNameMap = {};
                return;
            }

            const personIds = Object.keys(this.personLookup || {});

            if (!personIds.length) {
                return;
            }

            const entries = await Promise.all(personIds.map(async (personID) => {
                try {
                    const person = await getPersonById(personID);
                    const firstname = (person.firstname || "").trim();
                    const lastname = (person.lastname || "").trim();
                    const fullName = `${firstname} ${lastname}`.trim();

                    return [personID, fullName || personID];
                } catch (error) {
                    return [personID, personID];
                }
            }));

            this.personNameMap = entries.reduce((map, [personID, fullName]) => {
                map[personID] = fullName;
                return map;
            }, {});
        },
        refreshPersonLabels() {
            const svg = d3.select(this.$refs.networkCanvas).select("svg");

            if (svg.empty()) {
                return;
            }

            svg.selectAll(".labels text")
                .text((d) => this.getRenderedNodeLabel(d));
        },
        buildGraphData(records) {
            // Build two indexes: by person and by tester, then connect them with links
            // so the force simulation can render a bipartite-like network.
            const groupedByPerson = {};
            const groupedByTester = {};
            const testers = new Set(this.testerOrder);
            const links = [];
            let minWeight = Number.POSITIVE_INFINITY;
            let maxWeight = 0;

            records.forEach((record, index) => {
                const recordPersonKey = record.personID || record.matchKey;
                if (!recordPersonKey || !record.category) {
                    return;
                }

                if (String(record.category).trim().toLowerCase() === "y-dna") {
                    // The current network focuses on autosomal-style matches only.
                    return;
                }

                const normalizedTarget = this.resolveTesterNodeId(recordPersonKey);
                const parsedWeight = this.extractSharedDNAValue(record.dnaInfo && record.dnaInfo.sharedDNA);
                const normalizedLink = {
                    id: `${record.category}-${recordPersonKey}-${index}`,
                    source: record.category,
                    target: normalizedTarget,
                    tester: record.category,
                    personID: normalizedTarget,
                    dnaInfo: record.dnaInfo || {},
                    weightValue: parsedWeight.cmValue,
                    weightUnit: "cM",
                };

                if (normalizedLink.source === normalizedLink.target) {
                    return;
                }

                testers.add(record.category);
                if (this.isTesterNodeId(normalizedTarget)) {
                    testers.add(normalizedTarget);
                }
                links.push(normalizedLink);

                if (!this.isTesterNodeId(normalizedTarget)) {
                    if (!groupedByPerson[normalizedTarget]) {
                        groupedByPerson[normalizedTarget] = [];
                    }
                    groupedByPerson[normalizedTarget].push(normalizedLink);
                }

                if (!groupedByTester[record.category]) {
                    groupedByTester[record.category] = [];
                }
                groupedByTester[record.category].push(normalizedLink);

                if (normalizedLink.weightValue > 0) {
                    minWeight = Math.min(minWeight, normalizedLink.weightValue);
                    maxWeight = Math.max(maxWeight, normalizedLink.weightValue);
                }
            });

            const orderedTesters = [
                ...this.testerOrder.filter((testerID) => testers.has(testerID)),
                ...Array.from(testers).filter((testerID) => !this.testerOrder.includes(testerID)).sort((left, right) => left.localeCompare(right)),
            ];

            const testerNodes = orderedTesters
                .map((tester) => ({
                    id: tester,
                    type: "tester",
                    matchCount: (groupedByTester[tester] || []).length,
                }));

            const personNodes = Object.keys(groupedByPerson)
                .map((personID) => {
                    const matches = this.sortMatches(groupedByPerson[personID]);
                    const strongest = matches[0] || { tester: "", weightValue: 0, weightUnit: "cM" };

                    return {
                        id: personID,
                        type: "person",
                        matches,
                        testerSet: new Set(matches.map((match) => match.tester)),
                        highestWeight: strongest.weightValue,
                        highestWeightUnit: strongest.weightUnit,
                        highestTester: strongest.tester,
                    };
                })
                .sort((left, right) => this.comparePersonIds(left.id, right.id));

            const personLookup = personNodes.reduce((lookup, person) => {
                lookup[person.id] = person;
                return lookup;
            }, {});

            const testerLookup = testerNodes.reduce((lookup, tester) => {
                lookup[tester.id] = {
                    ...tester,
                    matches: this.sortMatches(groupedByTester[tester.id] || []),
                };
                return lookup;
            }, {});

            return {
                testerNodes,
                personNodes,
                personLookup,
                testerLookup,
                nodes: [...testerNodes, ...personNodes],
                links,
                minWeight: Number.isFinite(minWeight) ? minWeight : 0,
                maxWeight,
            };
        },
        createDNARelationNetwork(graphData) {
            if (!graphData) {
                return;
            }

            const container = this.$refs.networkCanvas;
            const width = container.offsetWidth;
            const height = container.offsetHeight;
            const scaleDomain = this.getScaleDomain(graphData.minWeight, graphData.maxWeight);
            const linkWidthScale = d3.scaleLinear().domain(scaleDomain).range([0.5, 6]).clamp(true);
            const personColorScale = d3
                .scaleSequential(d3.interpolateRgb("#fdd", "#ae1e25"))
                .domain([0, Math.max(graphData.maxWeight, 1)]);
            const labelDistanceFromNode = 12;

            this.assignTesterAnchors(graphData.nodes, width, height);

            // Use one root SVG and a nested graph layer so interactions can clear
            // selection state by clicking outside nodes/edges.
            const svgRoot = d3
                .select(this.$refs.networkCanvas)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${width} ${height}`)
                .attr("preserveAspectRatio", "xMidYMid meet")
                .on("click", () => {
                    this.clearSelection();
                });

            const graphLayer = svgRoot.append("g");

            // Links are weighted by shared DNA so visual thickness conveys strength.
            const link = graphLayer
                .append("g")
                .attr("class", "links")
                .selectAll("line")
                .data(graphData.links)
                .enter()
                .append("line")
                .attr("data-source", (d) => d.source)
                .attr("data-target", (d) => d.target)
                .attr("stroke", (d) => this.getTesterColor(d.tester))
                .attr("stroke-width", (d) => linkWidthScale(d.weightValue))
                .attr("stroke-linecap", "round")
                .attr("stroke-opacity", 0.35)
                .on("mouseenter", (event, d) => {
                    this.showEdgeTooltip(event, d);
                    d3.select(event.currentTarget).classed("hovered", true);
                    this.highlightSelection();
                })
                .on("mousemove", (event, d) => {
                    this.showEdgeTooltip(event, d);
                })
                .on("mouseleave", (event) => {
                    this.hideTooltip();
                    d3.select(event.currentTarget).classed("hovered", false);
                    this.highlightSelection();
                });

            // Nodes represent testers or persons; tester nodes are larger anchors.
            const node = graphLayer
                .append("g")
                .attr("class", "nodes")
                .selectAll("circle")
                .data(graphData.nodes)
                .enter()
                .append("circle")
                .attr("data-id", (d) => d.id)
                .attr("data-type", (d) => d.type)
                .attr("r", (d) => (d.type === "tester" ? 18 : 8))
                .attr("fill", (d) => {
                    if (d.type === "tester") {
                        return this.getTesterColor(d.id);
                    }

                    return personColorScale(d.highestWeight || 0);
                })
                .attr("stroke", (d) => {
                    return this.getNodeStrokeColor(d);
                })
                .attr("stroke-width", (d) => (d.type === "tester" ? 3 : this.getNodeStrokeWidth(d)))
                .call(
                    d3.drag()
                        .on("start", (event, d) => {
                            if (d.type === "tester") {
                                return;
                            }
                            if (!event.active) {
                                this.simulation.alphaTarget(0.3).restart();
                            }
                            d.fx = d.x;
                            d.fy = d.y;
                        })
                        .on("drag", (event, d) => {
                            if (d.type === "tester") {
                                return;
                            }
                            d.fx = event.x;
                            d.fy = event.y;
                        })
                        .on("end", (event, d) => {
                            if (d.type === "tester") {
                                return;
                            }
                            if (!event.active) {
                                this.simulation.alphaTarget(0);
                            }
                            d.fx = null;
                            d.fy = null;
                        })
                )
                .on("mouseenter", (event, d) => {
                    this.hoveredNode = { id: d.id, type: d.type };
                    graphLayer.selectAll(".links line")
                        .classed("hover-linked", (linkDatum) => {
                            if (!this.isLinkVisible(linkDatum)) {
                                return false;
                            }

                            const sourceId = linkDatum.source.id || linkDatum.source;
                            const targetId = linkDatum.target.id || linkDatum.target;
                            return sourceId === d.id || targetId === d.id;
                        });
                    this.refreshPersonLabels();
                    this.showNodeTooltip(event, d);
                })
                .on("mousemove", (event, d) => {
                    this.showNodeTooltip(event, d);
                })
                .on("mouseleave", () => {
                    this.hoveredNode = null;
                    graphLayer.selectAll(".links line").classed("hover-linked", false);
                    this.refreshPersonLabels();
                    this.hideTooltip();
                })
                .on("click", (event, d) => {
                    event.stopPropagation();
                    this.selectNode(d, d.type === "person");
                });

            // Labels adapt between compact/full versions depending on hover/selection.
            const label = graphLayer
                .append("g")
                .attr("class", "labels")
                .selectAll("text")
                .data(graphData.nodes)
                .enter()
                .append("text")
                .attr("data-id", (d) => d.id)
                .attr("data-type", (d) => d.type)
                .attr("dy", ".35em")
                .attr("x", (d) => d.x + this.getLabelOffsetX(d, labelDistanceFromNode))
                .style("font-size", (d) => (d.type === "tester" ? "13px" : "11px"))
                .style("font-weight", (d) => (d.type === "tester" ? 700 : 500))
                .style("text-anchor", (d) => this.getLabelTextAnchor(d))
                .style("pointer-events", "none")
                .text((d) => this.getRenderedNodeLabel(d));

            const testerOrbitRadius = this.getTesterOrbitRadius(width, height);

            // Force simulation keeps testers in a loose orbit while allowing person
            // nodes to settle by link strength and collision constraints.
            this.simulation = d3
                .forceSimulation(graphData.nodes)
                .force(
                    "link",
                    d3.forceLink(graphData.links)
                        .id((d) => d.id)
                        .distance(testerOrbitRadius * 0.5)
                        .strength(0.75)
                )
                .force("charge", d3.forceManyBody().strength(-60))
                .force("collision", d3.forceCollide().radius((d) => (d.type === "tester" ? 30 : 16)))
                .force("center", d3.forceCenter(width / 2, height / 2))
                .alphaDecay(0.045)
                .alphaMin(0.02)
                .on("tick", () => {
                    link
                        .attr("x1", (d) => d.source.x)
                        .attr("y1", (d) => d.source.y)
                        .attr("x2", (d) => d.target.x)
                        .attr("y2", (d) => d.target.y);

                    node
                        .attr("cx", (d) => d.x)
                        .attr("cy", (d) => d.y);

                    label
                        .attr("x", (d) => d.x + this.getLabelOffsetX(d, labelDistanceFromNode))
                        .attr("y", (d) => d.y)
                        .style("text-anchor", (d) => this.getLabelTextAnchor(d))
                        .attr("dx", (d) => this.getLabelDx(d))
                        .attr("textLength", null)
                        .attr("lengthAdjust", null);
                });

            this.applyTesterFilters();
            this.highlightSelection();
        },
        resizeNetwork() {
            const container = this.$refs.networkCanvas;
            const svg = d3.select(this.$refs.networkCanvas).select("svg");

            if (!container || svg.empty()) {
                return;
            }

            const width = container.offsetWidth;
            const height = container.offsetHeight;

            svg
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${width} ${height}`);

            if (this.simulation) {
                const testerOrbitRadius = this.getTesterOrbitRadius(width, height);
                this.simulation.force("center", d3.forceCenter(width / 2, height / 2));
                this.simulation.force("link").distance(testerOrbitRadius * 0.5);
                this.assignTesterAnchors(this.simulation.nodes(), width, height);
                this.simulation.alpha(0.25).restart();
            }
        },
        toggleTesterFilter(testerID) {
            this.activeTesters = {
                ...this.activeTesters,
                [testerID]: !this.activeTesters[testerID],
            };

            if (this.selectedNode && !this.isNodeVisible(this.selectedNode)) {
                this.clearSelection();
            }

            this.applyTesterFilters();
            this.highlightSelection();
        },
        selectPerson(personID, scrollToTop = false) {
            this.selectNode({ id: personID, type: "person" }, scrollToTop);
        },
        selectNode(node, scrollToTop = false) {
            const isSameSelection = this.selectedNode
                && this.selectedNode.id === node.id
                && this.selectedNode.type === node.type;

            if (isSameSelection) {
                this.clearSelection();
                return;
            }

            this.selectedNode = { id: node.id, type: node.type };
            this.refreshPersonLabels();
            this.highlightSelection();

            if (scrollToTop && node.type === "person") {
                this.$nextTick(() => {
                    const container = this.$el.querySelector(".dna-network-sidebar");
                    const highlightedItem = container && container.querySelector(".dna-network-person-item.highlighted");

                    if (container && highlightedItem) {
                        container.scrollTo({
                            top: highlightedItem.offsetTop - container.offsetTop,
                            behavior: "smooth",
                        });
                    }
                });
            }
        },
        clearSelection() {
            this.selectedNode = null;
            this.secondaryNodes = [];
            this.refreshPersonLabels();
            this.highlightSelection();
        },
        applyTesterFilters() {
            const svg = d3.select(this.$refs.networkCanvas).select("svg");

            if (svg.empty()) {
                return;
            }

            svg.selectAll(".links line")
                .classed("filtered-out", (d) => !this.isLinkVisible(d));

            svg.selectAll(".nodes circle")
                .classed("filtered-out", (d) => !this.isNodeVisible(d))
                .classed("tester-inactive", (d) => d.type === "tester" && !this.isTesterActive(d.id));

            svg.selectAll(".labels text")
                .classed("filtered-out", (d) => !this.isNodeVisible(d));
        },
        highlightSelection() {
            const svg = d3.select(this.$refs.networkCanvas).select("svg");

            if (svg.empty()) {
                return;
            }

            if (!this.selectedNode) {
                svg.selectAll(".nodes circle")
                    .classed("highlighted", false)
                    .classed("secondary", false)
                    .classed("dimmed", false);

                svg.selectAll(".links line")
                    .classed("highlighted", false)
                    .classed("secondary", false)
                    .classed("dimmed", false);

                svg.selectAll(".labels text")
                    .classed("dimmed", false)
                    .attr("fill", (d) => (d.type === "tester" ? "#111111" : "#4A4A4A"));

                return;
            }

            // Selection highlighting is computed from graph connectivity so both the
            // canvas and sidebar can show first-degree context around the chosen node.
            const selectedId = this.selectedNode.id;
            const selectedType = this.selectedNode.type;
            const secondaryNodesSet = new Set();

            svg.selectAll(".links line")
                .classed("highlighted", (d) => {
                    if (!this.isLinkVisible(d)) {
                        return false;
                    }

                    const sourceId = d.source.id || d.source;
                    const targetId = d.target.id || d.target;
                    const isConnected = sourceId === selectedId || targetId === selectedId;

                    if (isConnected) {
                        secondaryNodesSet.add(sourceId === selectedId ? targetId : sourceId);
                    }

                    return isConnected;
                })
                .classed("secondary", false)
                .classed("dimmed", (d) => {
                    if (!this.isLinkVisible(d)) {
                        return false;
                    }

                    const sourceId = d.source.id || d.source;
                    const targetId = d.target.id || d.target;
                    return sourceId !== selectedId && targetId !== selectedId;
                });

            svg.selectAll(".nodes circle")
                .classed("highlighted", (d) => d.id === selectedId)
                .classed("secondary", (d) => secondaryNodesSet.has(d.id))
                .classed("dimmed", (d) => this.isNodeVisible(d) && d.id !== selectedId && !secondaryNodesSet.has(d.id))
                .filter((d) => d.id === selectedId || secondaryNodesSet.has(d.id))
                .each(function () {
                    d3.select(this).raise();
                });

            svg.selectAll(".labels text")
                .classed("dimmed", (d) => this.isNodeVisible(d) && d.id !== selectedId && !secondaryNodesSet.has(d.id))
                .attr("fill", (d) => {
                    if (d.id === selectedId) {
                        return "#111111";
                    }
                    if (secondaryNodesSet.has(d.id)) {
                        return "#111111";
                    }
                    if (selectedType === "tester" && d.type === "tester") {
                        return "#555555";
                    }
                    return "#A9A9A9";
                })
                .filter((d) => d.id === selectedId || secondaryNodesSet.has(d.id))
                .each(function () {
                    d3.select(this).raise();
                });

            this.secondaryNodes = Array.from(secondaryNodesSet).sort((left, right) => left.localeCompare(right));
        },
        isSelectedPerson(personID) {
            return !!this.selectedNode && this.selectedNode.type === "person" && this.selectedNode.id === personID;
        },
        getPersonMatches(personID, applyFilter = false) {
            const person = this.personLookup[personID];

            if (!person) {
                return [];
            }

            if (!applyFilter) {
                return person.matches;
            }

            return person.matches.filter((match) => this.isTesterActive(match.tester));
        },
        getTesterSummary(testerID) {
            const tester = this.testerLookup[testerID];
            const matches = tester ? tester.matches.filter((match) => this.isTesterActive(match.tester)) : [];
            const strongest = matches[0] || { personID: null, weightValue: 0, weightUnit: "cM" };

            return {
                matchCount: matches.length,
                strongestMatchId: strongest.personID,
                strongestWeight: strongest.weightValue,
                strongestWeightUnit: strongest.weightUnit,
                topMatches: matches.slice(0, 6),
            };
        },
        getPersonDisplayName(personID) {
            const resolvedPersonID = this.resolveTesterNodeId(personID);

            if (this.isTesterNodeId(resolvedPersonID)) {
                return resolvedPersonID;
            }

            if (!this.isAuthenticated) {
                return this.$t('home.nameHiddenPrivacy');
            }

            return this.personNameMap[resolvedPersonID] || resolvedPersonID;
        },
        resolveTesterNodeId(personID) {
            return this.testerRootToId[personID] || personID;
        },
        isTesterNodeId(nodeID) {
            return Object.prototype.hasOwnProperty.call(this.testers, nodeID);
        },
        getScaleDomain(minWeight, maxWeight) {
            if (!Number.isFinite(minWeight) || !Number.isFinite(maxWeight) || maxWeight <= 0) {
                return [0, 1];
            }

            if (minWeight === maxWeight) {
                return [0, maxWeight];
            }

            return [minWeight, maxWeight];
        },
        extractSharedDNAValue(value) {
            const source = String(value || "").trim();

            if (!source || source === "-") {
                return { cmValue: 0 };
            }

            const inParentheses = source.match(/\(([^)]+)\)/);
            if (inParentheses) {
                const cmInParentheses = inParentheses[1].match(/([-\d.,]+)/);
                if (cmInParentheses) {
                    return { cmValue: this.parseLocalizedNumber(cmInParentheses[1]) };
                }
            }

            const percentMatch = source.match(/([-\d.,]+)\s*%/);
            if (percentMatch) {
                const percentValue = this.parseLocalizedNumber(percentMatch[1]);
                return { cmValue: (percentValue / 100) * 7100 };
            }

            const cmMatch = source.match(/([-\d.,]+)\s*cM/i);
            if (cmMatch) {
                return { cmValue: this.parseLocalizedNumber(cmMatch[1]) };
            }

            return { cmValue: 0 };
        },
        parseLocalizedNumber(value) {
            const normalized = String(value || "")
                .trim()
                .replace(/\s+/g, "");

            if (!normalized || normalized === "-") {
                return 0;
            }

            const commaIndex = normalized.lastIndexOf(",");
            const dotIndex = normalized.lastIndexOf(".");

            if (commaIndex > -1 && dotIndex > -1) {
                if (commaIndex > dotIndex) {
                    return Number(normalized.replace(/\./g, "").replace(",", ".")) || 0;
                }

                return Number(normalized.replace(/,/g, "")) || 0;
            }

            if (commaIndex > -1) {
                return Number(normalized.replace(/\./g, "").replace(",", ".")) || 0;
            }

            return Number(normalized.replace(/,/g, "")) || 0;
        },
        sortMatches(matches) {
            return [...matches].sort((left, right) => {
                if (right.weightValue !== left.weightValue) {
                    return right.weightValue - left.weightValue;
                }

                return left.tester.localeCompare(right.tester);
            });
        },
        comparePersonIds(left, right) {
            const leftValue = Number(String(left).replace(/\D+/g, ""));
            const rightValue = Number(String(right).replace(/\D+/g, ""));

            if (Number.isFinite(leftValue) && Number.isFinite(rightValue) && leftValue !== rightValue) {
                return leftValue - rightValue;
            }

            return left.localeCompare(right);
        },
        getTesterColor(testerID) {
            return this.testerColorMap[testerID] || DEFAULT_TESTER_COLOR;
        },
        getTesterStyle(testerID) {
            const index = this.testerOrder.indexOf(testerID);
            return index >= 0 ? DNA_TESTER_STYLES[index % DNA_TESTER_STYLES.length] : null;
        },
        getBadgeStyle(testerID) {
            const testerStyle = this.getTesterStyle(testerID);

            if (testerStyle) {
                return {
                    backgroundColor: testerStyle.badgeBg,
                    color: testerStyle.badgeText,
                };
            }

            const color = d3.color(this.getTesterColor(testerID));

            if (!color) {
                return { backgroundColor: DEFAULT_TESTER_COLOR, color: "#ffffff" };
            }

            return {
                backgroundColor: color.formatHex(),
                color: "#ffffff",
            };
        },
        getMatchLabelStyle(testerID) {
            const testerStyle = this.getTesterStyle(testerID);

            if (testerStyle) {
                return {
                    backgroundColor: testerStyle.badgeBg,
                    color: testerStyle.badgeText,
                };
            }

            const color = d3.color(this.getTesterColor(testerID));

            if (!color) {
                return {};
            }

            return {
                backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, 0.18)`,
                color: color.darker(1.2).formatHex(),
            };
        },
        getTesterDotStyle(testerID) {
            return {
                backgroundColor: this.getTesterColor(testerID),
            };
        },
        isTesterActive(testerID) {
            return this.activeTesters[testerID] !== false;
        },
        isPersonVisible(personID) {
            const person = this.personLookup[personID];

            if (!person) {
                return false;
            }

            return person.matches.some((match) => this.isTesterActive(match.tester));
        },
        isLinkVisible(link) {
            return this.isTesterActive(link.tester);
        },
        isNodeVisible(node) {
            if (node.type === "tester") {
                return true;
            }

            return this.isPersonVisible(node.id);
        },
        getNodeStrokeColor(node) {
            if (node.type === "tester") {
                return d3.color(this.getTesterColor(node.id)).darker(0.8).formatHex();
            }

            return this.getTesterColor(node.highestTester);
        },
        getNodeStrokeWidth(node) {
            if (node.type === "tester") {
                return 3;
            }

            return node.testerSet && node.testerSet.size > 1 ? 2.25 : 1.5;
        },
        getNodeRadius(node) {
            return node.type === "tester" ? 18 : 8;
        },
        getRenderedNodeLabel(node) {
            if (node.type === "tester") {
                return node.id;
            }

            if (this.shouldShowFullPersonLabel(node.id)) {
                return this.getPersonDisplayName(node.id);
            }

            return this.getCompactPersonLabel(node.id);
        },
        getCompactPersonLabel(personID) {
            const fullName = this.getPersonDisplayName(personID);

            if (!this.isAuthenticated || fullName === personID) {
                return personID;
            }

            const parts = fullName.split(/\s+/).filter(Boolean);
            if (!parts.length) {
                return personID;
            }

            if (parts.length === 1) {
                return parts[0];
            }

            const firstName = parts[0];
            const lastNameInitial = parts[parts.length - 1].charAt(0);
            return lastNameInitial ? `${firstName} ${lastNameInitial}.` : firstName;
        },
        shouldShowFullPersonLabel(personID) {
            if (!this.isAuthenticated) {
                return false;
            }

            const isSelected = this.selectedNode && this.selectedNode.type === "person" && this.selectedNode.id === personID;
            const isHovered = this.hoveredNode && this.hoveredNode.type === "person" && this.hoveredNode.id === personID;
            return Boolean(isSelected || isHovered);
        },
        getNearestConnectedTester(node) {
            if (node.type !== "person" || !node.matches || !node.matches.length) {
                return null;
            }

            let nearestTester = null;
            let nearestDistance = Number.POSITIVE_INFINITY;

            node.matches.forEach((match) => {
                const testerPosition = this.testerAnchorMap[match.tester];
                if (!testerPosition) {
                    return;
                }

                const deltaX = node.x - testerPosition.x;
                const deltaY = node.y - testerPosition.y;
                const distance = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));

                if (distance < nearestDistance) {
                    nearestDistance = distance;
                    nearestTester = {
                        ...testerPosition,
                        distance,
                    };
                }
            });

            return nearestTester;
        },
        getLabelPlacement(node) {
            const defaultOffset = node.type === "tester" ? 22 : 12;

            if (node.type !== "person") {
                return {
                    offsetX: defaultOffset,
                    textAnchor: "start",
                    dx: 0,
                };
            }

            const nearestTester = this.getNearestConnectedTester(node);
            if (!nearestTester || nearestTester.distance > 72) {
                return {
                    offsetX: defaultOffset,
                    textAnchor: "start",
                    dx: 0,
                };
            }

            const deltaX = node.x - nearestTester.x;
            const horizontalDirection = deltaX >= 0 ? 1 : -1;
            const offsetMagnitude = this.getNodeRadius(node) + 12;

            return {
                offsetX: horizontalDirection * offsetMagnitude,
                textAnchor: horizontalDirection > 0 ? "start" : "end",
                dx: horizontalDirection > 0 ? 0 : 0,
            };
        },
        getLabelOffsetX(node) {
            return this.getLabelPlacement(node).offsetX;
        },
        getLabelTextAnchor(node) {
            return this.getLabelPlacement(node).textAnchor;
        },
        getLabelDx(node) {
            return this.getLabelPlacement(node).dx;
        },
        assignTesterColors(testerIDs) {
            return testerIDs.reduce((acc, testerID, index) => {
                acc[testerID] = this.getTesterStyle(testerID)?.accent || d3.schemeTableau10[index % d3.schemeTableau10.length] || DEFAULT_TESTER_COLOR;
                return acc;
            }, {});
        },
        getTesterOrbitRadius(width, height) {
            return Math.min(180, Math.max(120, Math.min(width, height) * 0.22)) * 1.5;
        },
        assignTesterAnchors(nodes, width, height) {
            const testerNodes = nodes.filter((node) => node.type === "tester");
            const anchoredTesterNodes = [...testerNodes];

            const radius = this.getTesterOrbitRadius(width, height);
            const centerX = width / 2;
            const centerY = height / 2;

            this.testerAnchorMap = {};

            anchoredTesterNodes.forEach((testerNode, index) => {
                const rotationOffset = Math.PI / 6;
                const angle = ((Math.PI * 2) / Math.max(anchoredTesterNodes.length, 1)) * index - Math.PI / 2 + rotationOffset;
                const x = centerX + radius * Math.cos(angle);
                const y = centerY + radius * Math.sin(angle);

                testerNode.fx = x;
                testerNode.fy = y;
                testerNode.x = x;
                testerNode.y = y;

                this.testerAnchorMap[testerNode.id] = { x, y };
            });
        },
        showNodeTooltip(event, node) {
            if (node.type === "person") {
                const matches = this.getPersonMatches(node.id, false);
                const personName = this.escapeHtml(this.getPersonDisplayName(node.id));
                const listHtml = matches
                    .map((match) => {
                        const tester = this.escapeHtml(match.tester);
                        const relation = this.escapeHtml(this.translateRelation(match.dnaInfo.relation || "-"));
                        const sharedDNA = this.escapeHtml(match.dnaInfo.sharedDNA || "-");
                        const sharedSegments = this.escapeHtml(match.dnaInfo.sharedSegments || "-");
                        const largestSegment = this.escapeHtml(match.dnaInfo.largestSegment || "-");

                        return `<div class="tooltip-row"><strong>${tester}</strong><br>${this.escapeHtml(this.$t('dnaMatch.relation'))}: ${relation}<br>${this.escapeHtml(this.$t('dnaMatch.sharedDNA'))}: ${sharedDNA}<br>${this.escapeHtml(this.$t('dnaMatch.segments'))}: ${sharedSegments}<br>${this.escapeHtml(this.$t('dnaMatch.largestSegment'))}: ${largestSegment}</div>`;
                    })
                    .join("");

                this.showTooltip(event, `<p><strong>${personName}</strong></p>${listHtml}`);
                return;
            }

            const testerSummary = this.getTesterSummary(node.id);
            this.showTooltip(
                event,
                `<p><strong>${this.escapeHtml(node.id)}</strong></p><p>Matches: ${testerSummary.matchCount}</p>`,
            );
        },
        showEdgeTooltip(event, edge) {
            const tester = this.escapeHtml(edge.tester || "-");
            const personID = edge.personID || edge.target.id || edge.target || "-";
            const personName = this.escapeHtml(this.getPersonDisplayName(personID));
            const relation = this.escapeHtml(this.translateRelation((edge.dnaInfo && edge.dnaInfo.relation) || "-"));
            const sharedDNA = this.escapeHtml((edge.dnaInfo && edge.dnaInfo.sharedDNA) || "-");

            this.showTooltip(
                event,
                `<p><strong>${tester} -> ${personName}</strong></p><p>${this.escapeHtml(this.$t('dnaMatch.relation'))}: ${relation}</p><p>${this.escapeHtml(this.$t('dnaMatch.sharedDNA'))}: ${sharedDNA}</p>`,
            );
        },
        showTooltip(event, content) {
            this.tooltip.visible = true;
            this.tooltip.content = content;
            this.updateTooltipPosition(event);
        },
        updateTooltipPosition(event) {
            if (!event) {
                return;
            }

            const rect = this.$el.getBoundingClientRect();
            const x = event.clientX - rect.left + 14;
            const y = event.clientY - rect.top + 14;
            const maxX = Math.max(this.$el.clientWidth - 280, 20);
            const maxY = Math.max(this.$el.clientHeight - 220, 20);

            this.tooltip.x = Math.min(x, maxX);
            this.tooltip.y = Math.min(y, maxY);
        },
        hideTooltip() {
            this.tooltip.visible = false;
            this.tooltip.content = "";
        },
        escapeHtml(value) {
            return String(value || "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        },
        formatWeight(value, unit) {
            if (!value) {
                return "-";
            }

            const formattedValue = new Intl.NumberFormat("nl-NL", {
                minimumFractionDigits: value % 1 === 0 ? 0 : 1,
                maximumFractionDigits: 1,
            }).format(value);

            if (unit === "%") {
                return `${formattedValue}%`;
            }

            return `${formattedValue} cM`;
        },
    },
};
</script>

<style>
.dna-relation-network {
    position: relative;
    display: block;
    height: 700px;
    width: 100vw;
    max-width: 100vw;
    overflow: hidden;
    background: transparent;
}

.dna-network-canvas {
    /* width: 100vw; */
    height: 100%;
    overflow: hidden;
    background: transparent;
}

.dna-network-sidebar {
    display: none;
    position: absolute;
    left: 0;
    top: 0;
    width: 286px;
    height: 100%;
    overflow-y: auto;
    background-color: rgba(255, 255, 255, 0.96);
    border-right: 1px solid #f0f0f0;
    z-index: 40;
}

.dna-network-sidebar h1 {
    font-family: var(--font-primary);
    color: var(--brand-color-1);
    font-size: 16px;
    font-weight: 700;
    line-height: 125%;
    text-transform: lowercase;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.dna-network-sidebar-intro {
    padding: 12px 24px 16px;
    border-bottom: 1px solid #f0f0f0;
}

.dna-network-sidebar-intro p {
    margin: 0;
    color: #6f6f6f;
    font-size: 12px;
}

.dna-network-tester-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.dna-network-tester-toggle {
    border: none;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, opacity 0.2s ease;
}

.dna-network-tester-toggle:hover {
    transform: translateY(-1px);
}

.dna-network-tester-toggle.inactive {
    opacity: 0.35;
}

.dna-network-sidebar-search {
    padding: 12px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.dna-network-sidebar-search input {
    width: 100%;
    border: 1px solid #e2dfdd;
    border-radius: 10px;
    padding: 9px 11px;
    font-size: 13px;
    background: #ffffff;
    color: #2a2a2a;
}

.dna-network-sidebar-search input:focus {
    outline: none;
    border-color: #c57a7d;
    box-shadow: 0 0 0 2px rgba(174, 30, 37, 0.12);
}

.dna-network-person-item {
    position: relative;
    padding: 14px 24px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s ease;
}

.dna-network-person-item:hover {
    background-color: #f8f4f3;
}

.dna-network-person-item.highlighted {
    background-color: #f5efee;
}

.dna-network-person-item-main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.dna-network-person-id {
    margin: 0;
    color: #1f1f1f;
    font-weight: 700;
}

.dna-network-person-meta {
    margin: 4px 0 0;
    color: #696969;
    font-size: 12px;
    line-height: 1.5;
}

.dna-network-person-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 24px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
}

.dna-network-person-expanded {
    margin-top: 16px;
}

.dna-network-secondary-label {
    margin: 0 0 8px;
    color: var(--brand-text-dark);
    font-size: 12px;
}

.dna-network-secondary-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
}

.dna-network-secondary-item {
    padding: 4px 10px;
    border-radius: 999px;
    background: #f6f6f6;
    font-size: 12px;
    color: #444;
}

.dna-network-match-groups {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dna-network-match-groups--panel {
    max-height: 50vh;
    overflow: auto;
    padding-right: 4px;
}

.dna-network-match-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dna-network-match-group-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.dna-network-color-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    flex-shrink: 0;
}

.dna-network-match-group-data {
    display: grid;
    gap: 6px;
    padding-left: 18px;
    border-left: 1px solid #dedede;
}

.dna-network-match-group-data p,
.dna-network-tester-summary p {
    margin: 0;
    color: #494949;
    font-size: 13px;
    line-height: 1.5;
}

.dna-network-details-panel {
    position: absolute;
    right: 24px;
    top: 72px;
    width: min(360px, calc(100vw - 48px));
    max-height: calc(100vh - 120px);
    overflow: auto;
    padding: 20px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(12px);
    box-shadow: var(--shadow-card-lg);
    z-index: 30;
}

.dna-network-details-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
}

.dna-network-details-eyebrow {
    margin: 0 0 4px;
    color: #7d7d7d;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.dna-network-details-header h2 {
    margin: 0;
    color: #111111;
    font-family: var(--font-primary);
    font-size: 24px;
}

.dna-network-details-summary {
    margin: 0;
    color: var(--brand-color-1);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.dna-network-tester-summary {
    display: grid;
    gap: 8px;
    margin-bottom: 16px;
}

.dna-network-top-matches {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 8px;
}

.dna-network-top-matches li {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 12px;
    border-radius: var(--border-radius-lg);
    background: #faf6f5;
    color: #333333;
    font-size: 13px;
}

.dna-relation-network .links line {
    stroke-opacity: 0.35;
    transition: stroke-opacity 0.2s ease;
}

.dna-relation-network .nodes circle {
    cursor: pointer;
    transition: opacity 0.2s ease, stroke-width 0.2s ease;
}

.dna-relation-network .nodes circle.tester-inactive {
    opacity: 0.4;
}

.dna-relation-network .nodes circle.highlighted {
    stroke: #111111 !important;
    stroke-width: 3px !important;
    opacity: 1 !important;
}

.dna-relation-network .nodes circle.secondary {
    stroke: #111111 !important;
    stroke-width: 2px !important;
    opacity: 1 !important;
}

.dna-relation-network .nodes circle.dimmed,
.dna-relation-network .links line.dimmed,
.dna-relation-network .labels text.dimmed {
    opacity: 0.12 !important;
}

.dna-relation-network .links line.highlighted,
.dna-relation-network .links line.hovered,
.dna-relation-network .links line.hover-linked {
    stroke-opacity: 1 !important;
}

.dna-relation-network .labels text {
    font-family: Arial, sans-serif;
    transition: opacity 0.2s ease, fill 0.2s ease;
}

.dna-relation-network .nodes circle.filtered-out,
.dna-relation-network .links line.filtered-out,
.dna-relation-network .labels text.filtered-out {
    display: none;
}

.dna-network-tooltip {
    position: absolute;
    z-index: 120;
    width: min(260px, calc(100vw - 24px));
    max-height: 55vh;
    overflow: auto;
    padding: 10px 12px;
    border-radius: var(--border-radius-lg);
    background: rgba(255, 255, 255, 0.97);
    border: 1px solid #e8e1df;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.16);
    pointer-events: none;
    font-size: 12px;
    color: #2c2c2c;
    line-height: 1.45;
}

.dna-network-tooltip p {
    margin: 0 0 6px;
}

.dna-network-tooltip .tooltip-row {
    margin-bottom: 8px;
    padding-top: 8px;
    border-top: 1px solid #ece7e5;
}

@media (max-width: 1100px) {
    .dna-network-details-panel {
        width: min(320px, calc(100vw - 32px));
        right: 16px;
        top: 64px;
    }
}

@media (max-width: 860px) {

    .dna-network-sidebar {
        display: none;
    }

    .dna-network-details-panel {
        left: 16px;
        right: 16px;
        top: auto;
        bottom: 72px;
        width: auto;
        max-height: 38vh;
    }
}
</style>
